// `payment_settings` — the application's own "connect your gateway" screen,
// as a block a designer can put on a page in its navigation.
//
// The sibling `payment_checkout` block SPENDS this configuration; this one
// CAPTURES it. Both read the same row: what an administrator saves here is
// what the checkout finds at `/api/app-payments/{owner}/{project}/config` the
// next time a customer pays. Nothing is copied between them.
//
// THE FORM IS NOT WRITTEN HERE. It is `GatewaySetupForm`, shared verbatim with
// the builder's Advanced → Payments screen, so the two surfaces cannot come to
// disagree about what is a secret, what a save sends, or what "connected"
// means. The only thing this file decides is WHICH endpoints it writes
// through — the app-facing ones, gated on the project owner or FUNC-ADMIN and
// reachable on a customer's own domain.
//
// SECRETS NEVER COME BACK. A read returns a masked tail only; an omitted
// secret means "unchanged" and "-" clears. That is enforced server-side in
// `gateway_config`, not here, so a mistake in this file cannot leak one.
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Loader2, Lock } from 'lucide-react'

import {
  deleteAppGateway, listAppGateways, saveAppGateway, setAppCashOnDelivery,
  testAppGateway,
} from '@/api/appGatewaySettings'
import type { GatewayListResponse, GatewayProvider } from '@/api/appPayments'
import {
  blankGateway, GatewaySetupForm, type GatewaySetupApi,
} from '@/pages/payments/GatewaySetupForm'
import { useRuntime } from '@/pages/workbench/smartpage-preview/RuntimeContext'
import type { PaymentSettingsBlock } from '@/pages/designers/page/types'
import { injectEcomStyles } from './styles'

/** The app-facing router — see `appGatewaySettings` for why it is a separate
 *  one from the builder's. */
const APP_GATEWAY_API: GatewaySetupApi = {
  save: saveAppGateway,
  test: testAppGateway,
  remove: deleteAppGateway,
  setCod: setAppCashOnDelivery,
}

export default function PaymentSettingsRenderer(
  { block, project: projectProp }: {
    block: PaymentSettingsBlock
    /** Set by a host that renders the form OUTSIDE a page — the Run view's
     *  System administration → Payments tab — where there is no page runtime
     *  to name the application. A page block leaves it unset. */
    project?: string
  },
) {
  injectEcomStyles()
  const runtime = useRuntime()
  const project = projectProp || runtime.project

  const [data, setData] = useState<GatewayListResponse | null>(null)
  const [provider, setProvider] = useState<GatewayProvider>(
    (block.defaultProvider as GatewayProvider) || 'razorpay')
  const [loading, setLoading] = useState(true)
  // The server decides who may configure a gateway; this only decides what to
  // SHOW someone who may not, so the page reads as "not yours to change"
  // rather than as a broken block.
  const [refused, setRefused] = useState('')

  const load = useCallback(async () => {
    if (!project) return
    setLoading(true)
    try {
      setData(await listAppGateways(project))
      setRefused('')
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status
      const detail = (err as { response?: { data?: { detail?: unknown } } })
        ?.response?.data?.detail
      setRefused(
        status === 403 || status === 404
          ? (typeof detail === 'string' && detail
            ? detail
            : 'You do not have permission to configure payments for this application.')
          : 'Could not load the payment settings.',
      )
    } finally {
      setLoading(false)
    }
  }, [project])

  useEffect(() => { void load() }, [load])

  const gateway = useMemo(() => {
    const found = data?.gateways.find(g => g.provider === provider)
    return found || blankGateway(provider)
  }, [data, provider])

  if (!project) {
    return (
      <div className="paychk__note" data-ecom-block="payment_settings">
        <strong>Payment settings</strong> — this surface does not name an
        application, so there is nothing to configure here.
      </div>
    )
  }

  if (loading && !data) {
    return (
      <div className="paychk__note" data-ecom-block="payment_settings"
           style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <Loader2 size={16} className="spin" /> Loading payment settings…
      </div>
    )
  }

  if (refused) {
    return (
      <div className="paychk__note" data-ecom-block="payment_settings"
           style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <Lock size={16} style={{ marginTop: 2, flex: '0 0 16px' }} />
        <span>{refused}</span>
      </div>
    )
  }

  return (
    <section data-ecom-block="payment_settings">
      {block.heading && (
        <h3 style={{ margin: '0 0 4px', fontSize: '1.05rem', fontWeight: 600 }}>
          {block.heading}
        </h3>
      )}
      <p style={{
        color: 'var(--pg-text-secondary, var(--color-text-secondary))',
        fontSize: 13, margin: '0 0 16px',
      }}>
        {block.description
          || 'Connect the merchant account this application charges on. Money '
             + 'taken by the application settles to you. Until a gateway is '
             + 'connected and switched on, the application can still take cash '
             + 'on delivery.'}
      </p>
      <GatewaySetupForm
        api={APP_GATEWAY_API}
        project={project}
        data={data}
        provider={provider}
        onProvider={setProvider}
        gateway={gateway}
        onSaved={load}
      />
    </section>
  )
}
